/**
 * Assistant screen (WO-25).
 *
 * Wraps AssistantPane with the standard screen chrome (header + back nav).
 *
 * tenancyUnitId: falls back to session user's organizationId.
 * ASSUMPTION — see AssistantPane module doc: confirm whether a jobId /
 * estimateId is the intended tenancyUnitId. The organizationId is a safe
 * org-level fallback that the backend ScopeResolver already handles via JWT,
 * but finer-grained context may be desirable once job/estimate IDs are wired
 * on mobile (WO-M4 / WO-15).
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useSession } from '../../src/auth/session';
import { Screen, Text, Button } from '../../src/components/ui';
import { AssistantPane } from '../../src/components/assistant/AssistantPane';
import { theme } from '../../src/theme';

export default function AssistantScreen() {
  const router = useRouter();
  const user = useSession((s) => s.user);

  /**
   * ASSUMPTION: using organizationId as tenancyUnitId.
   * If the session user has no organization (edge case during loading), render
   * a fallback. In practice, useSession is authenticated before this screen renders.
   */
  const tenancyUnitId = user?.organizationId ?? '';

  if (!tenancyUnitId) {
    return (
      <Screen padded>
        <View style={styles.centeredState} accessibilityRole="alert">
          <Text variant="body" color="danger" style={styles.centered}>
            Session not ready. Please go back and try again.
          </Text>
        </View>
      </Screen>
    );
  }

  return (
    <Screen padded={false} edges={['top', 'left', 'right']}>
      {/* Screen header */}
      <View style={styles.header}>
        <Button
          title="Back"
          variant="ghost"
          size="sm"
          fullWidth={false}
          onPress={() => router.back()}
          accessibilityLabel="Go back"
          style={styles.backBtn}
        />
        <Text variant="subhead">Assistant</Text>
        {/* Spacer to centre the title */}
        <View style={styles.backBtn} />
      </View>

      <AssistantPane tenancyUnitId={tenancyUnitId} />
    </Screen>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.layout.screenPadding,
    paddingVertical: theme.spacing.md,
    backgroundColor: theme.color.bg.surface,
    borderBottomWidth: theme.layout.hairline,
    borderBottomColor: theme.color.border.default,
  },
  backBtn: {
    minWidth: theme.spacing['3xl'],
  },
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: theme.spacing['3xl'],
  },
  centered: {
    textAlign: 'center',
  },
});
